import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Loader2, Building2, DollarSign, MapPin, Wrench, TrendingUp, CheckCircle2, Check,
} from 'lucide-react';
import {
  SUPPORTED_COUNTRIES,
  resolveCompanyLocale,
  displayDistanceToKm,
  isValidPostcode,
  type CompanyLocale,
} from '@/utils/locale';
import { normalizePhoneE164 } from '@/utils/phone';
import { geocodeAddress } from '@/utils/geocodeAddress';

// ─── Schemas ────────────────────────────────────────────────────────────────

const makeStep1Schema = (locale: CompanyLocale) => z.object({
  country: z.string().min(1, 'Select your country'),
  name: z.string().min(1, 'Company name is required'),
  abn: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  street_address: z.string().optional(),
  suburb: z.string().optional(),
  state: z.string().optional(),
  postcode: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine((v) => isValidPostcode(v, locale), locale.postcode.message),
});
type Step1Data = z.infer<ReturnType<typeof makeStep1Schema>>;

const step2Schema = z.object({
  tax_enabled: z.boolean(),
  tax_rate: z.number().min(0).max(100).optional(),
  tax_label: z.string().optional(),
  tax_inclusive: z.boolean().optional(),
  parts_markup_pct: z.number().min(0).max(1000).optional(),
});

const step3Schema = z.object({
  metro_name: z.string().min(1, 'Service area name is required'),
  metro_address: z.string().optional(),
  metro_radius: z.coerce.number().min(0).optional(),
});

const step4Schema = z.object({
  tech_name: z.string().min(1, 'Technician name is required'),
  commission_percentage: z.coerce.number().min(0).max(100),
  tech_phone: z.string().optional(),
  tech_email: z.string().email('Invalid email').optional().or(z.literal('')),
});

type Step = 1 | 2 | 3 | 4 | 5 | 6;

/** Locale tax defaults in the shape of the step-2 form. */
const taxFormDefaults = (loc: CompanyLocale) => ({
  tax_enabled: loc.taxDefaults.enabled,
  tax_rate: loc.taxDefaults.rate,
  tax_label: loc.taxDefaults.label,
  tax_inclusive: loc.taxDefaults.inclusive,
});

const STEP_LABELS = ['Company', 'Tax', 'Service Area', 'Technician', 'Google Ads'];

// ─── Component ───────────────────────────────────────────────────────────────

export default function CompanySetup() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId: companyId, company, companyQueryKey } = useTenant();
  const [step, setStep] = useState<Step>(1);
  const [metroId, setMetroId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [googleAdsYes, setGoogleAdsYes] = useState(false);

  const setupDone = !!company?.setup_completed_at;
  useEffect(() => {
    if (step === 1 && setupDone) navigate('/');
  }, [step, setupDone, navigate]);

  // Marks the wizard finished (or skipped) so ProtectedRoute stops redirecting here.
  const completeSetup = async () => {
    if (!companyId) return;
    const { error } = await supabase.from('companies').update({ setup_completed_at: new Date().toISOString() }).eq('id', companyId);
    if (error) toast.error(error.message);
    await invalidateCompany();
  };

  const invalidateCompany = () => queryClient.invalidateQueries({ queryKey: companyQueryKey });

  // ─── Country drives everything else on the wizard ──────────────────────────
  // The founder invite may already have set the country; otherwise start on AU.
  const [country, setCountry] = useState<string>(() => resolveCompanyLocale(company?.country).name);
  const locale = useMemo(() => resolveCompanyLocale(country), [country]);
  const step1Schema = useMemo(() => makeStep1Schema(locale), [locale]);

  // ─── Forms ─────────────────────────────────────────────────────────────────

  const form1 = useForm<Step1Data>({
    resolver: zodResolver(step1Schema),
    defaultValues: { country, name: company?.name && company.name !== 'My Company' ? company.name : '', abn: '', phone: '', email: '', street_address: '', suburb: '', state: '', postcode: '' },
  });

  const form2 = useForm<z.infer<typeof step2Schema>>({
    resolver: zodResolver(step2Schema),
    defaultValues: taxFormDefaults(locale),
  });
  const taxEnabled = form2.watch('tax_enabled');

  const form3 = useForm<z.infer<typeof step3Schema>>({
    resolver: zodResolver(step3Schema),
    defaultValues: { metro_name: '', metro_address: '', metro_radius: locale.distanceUnit === 'mi' ? 10 : 15 },
  });

  const form4 = useForm<z.infer<typeof step4Schema>>({
    resolver: zodResolver(step4Schema),
    defaultValues: { tech_name: '', commission_percentage: 20, tech_phone: '', tech_email: '' },
  });

  // Once the company row arrives, pick up a country the invite already recorded.
  useEffect(() => {
    if (company?.country) {
      const name = resolveCompanyLocale(company.country).name;
      setCountry(name);
      form1.setValue('country', name);
    }
    if (company?.name && company.name !== 'My Company' && !form1.getValues('name')) form1.setValue('name', company.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.country]);

  const handleCountryChange = (name: string) => {
    setCountry(name);
    form1.setValue('country', name, { shouldValidate: true });
    const next = resolveCompanyLocale(name);
    form2.reset(taxFormDefaults(next));
    form3.setValue('metro_radius', next.distanceUnit === 'mi' ? 10 : 15);
  };

  // ─── Save handlers ─────────────────────────────────────────────────────────

  const onStep1 = async (data: Step1Data) => {
    if (!companyId) return;
    setIsSaving(true);
    try {
      const chosen = resolveCompanyLocale(data.country);
      const { error } = await supabase.from('companies').update({
        name: data.name,
        country: chosen.name,
        timezone: company?.timezone && company.timezone !== 'Australia/Sydney' ? company.timezone : chosen.defaultTimezone,
        abn: data.abn ? data.abn.replace(/\s/g, '') : null,
        phone: data.phone ? normalizePhoneE164(data.phone, chosen) : null,
        email: data.email || null,
        street_address: data.street_address || null,
        suburb: data.suburb || null,
        state: data.state || null,
        postcode: data.postcode || null,
      }).eq('id', companyId);
      if (error) throw error;
      setStep(2);
      void invalidateCompany();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const onStep2 = async (data: z.infer<typeof step2Schema>) => {
    if (!companyId) return;
    setIsSaving(true);
    try {
      const { error } = await supabase.from('companies').update({
        tax_enabled: data.tax_enabled,
        tax_rate: data.tax_enabled ? (data.tax_rate ?? locale.taxDefaults.rate) : 0,
        tax_label: data.tax_label || locale.taxDefaults.label,
        tax_inclusive: data.tax_inclusive ?? false,
        parts_markup_pct: data.parts_markup_pct ?? 0,
      }).eq('id', companyId);
      if (error) throw error;
      setStep(3);
      void invalidateCompany();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const onStep3 = async (data: z.infer<typeof step3Schema>) => {
    setIsSaving(true);
    try {
      const radius_km = data.metro_radius ? displayDistanceToKm(data.metro_radius, locale) : null;
      // A service area only matches jobs when it has a centre: geocode the address now.
      let center_lat: number | null = null;
      let center_lng: number | null = null;
      if (data.metro_address) {
        const coords = await geocodeAddress(data.metro_address, locale.countryCode);
        if (coords) {
          center_lat = coords.lat;
          center_lng = coords.lng;
        } else {
          toast.warning('Could not locate that address — set the service area centre later in Settings → Metros.');
        }
      }
      const { data: metro, error } = await supabase
        .from('metros')
        .insert({ name: data.metro_name, description: data.metro_address || null, radius_km, center_lat, center_lng })
        .select('id')
        .single();
      if (error) throw error;
      if (metro?.id) setMetroId(metro.id);
      setStep(4);
    } catch (e: any) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const onStep4 = async (data: z.infer<typeof step4Schema>) => {
    setIsSaving(true);
    try {
      const { data: tech, error } = await supabase
        .from('techs')
        .insert({
          name: data.tech_name,
          commission_percentage: data.commission_percentage,
          phone: data.tech_phone ? normalizePhoneE164(data.tech_phone, locale) : null,
          email: data.tech_email || null,
        })
        .select('id')
        .single();
      if (error) throw error;
      if (tech?.id && metroId) {
        await supabase.rpc('set_tech_metros', { p_tech_id: tech.id, p_metro_ids: [metroId] });
      }
      setStep(5);
    } catch (e: any) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Progress indicator ────────────────────────────────────────────────────

  const isComplete = step === 6;

  const progressDots = (
    <div className="flex items-center justify-center gap-1 mb-8">
      {STEP_LABELS.map((_, i) => {
        const n = (i + 1) as Step;
        const done = isComplete || step > n;
        const active = step === n;
        return (
          <div key={n} className="flex items-center gap-1">
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors',
                done
                  ? 'bg-primary text-primary-foreground'
                  : active
                  ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {done ? <Check className="w-3.5 h-3.5" /> : n}
            </div>
            {n < 5 && (
              <div className={cn('h-0.5 w-6', done ? 'bg-primary' : 'bg-muted')} />
            )}
          </div>
        );
      })}
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-lg">
        {!isComplete && progressDots}

        {/* ── Step 1: Company ── */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="w-5 h-5 text-primary" />
                <CardTitle>Your Company</CardTitle>
              </div>
              <CardDescription>Tell us about your business. You can update this anytime in Settings.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={form1.handleSubmit(onStep1)} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="country">
                    Country <span className="text-destructive">*</span>
                  </Label>
                  <Select value={country} onValueChange={handleCountryChange}>
                    <SelectTrigger id="country">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPORTED_COUNTRIES.map((c) => (
                        <SelectItem key={c.countryCode} value={c.name}>
                          {c.name} ({c.currency})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Sets your currency ({locale.currency}), tax defaults, address format and timezone.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="name">
                    Company Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="name" placeholder="Acme Services" {...form1.register('name')} />
                  {form1.formState.errors.name && (
                    <p className="text-xs text-destructive">{form1.formState.errors.name.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="abn">{locale.taxIdLabels.abn}</Label>
                    <Input id="abn" placeholder={locale.taxIdPlaceholder} {...form1.register('abn')} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" type="tel" placeholder={locale.phonePlaceholder} {...form1.register('phone')} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email">Business Email</Label>
                  <Input id="email" type="email" placeholder="info@company.com" {...form1.register('email')} />
                  {form1.formState.errors.email && (
                    <p className="text-xs text-destructive">{form1.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="street_address">Street Address</Label>
                  <Input id="street_address" placeholder="123 Main Street" {...form1.register('street_address')} />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="suburb">{locale.addressLabels.locality}</Label>
                    <Input id="suburb" placeholder={locale.addressPlaceholders.locality} {...form1.register('suburb')} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="state">{locale.addressLabels.region}</Label>
                    <Input id="state" placeholder={locale.addressPlaceholders.region} {...form1.register('state')} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="postcode">{locale.addressLabels.postcode}</Label>
                    <Input
                      id="postcode"
                      placeholder={locale.postcode.placeholder}
                      maxLength={locale.postcode.maxLength}
                      {...form1.register('postcode')}
                    />
                    {form1.formState.errors.postcode && (
                      <p className="text-xs text-destructive">{form1.formState.errors.postcode.message}</p>
                    )}
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isSaving}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Continue
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* ── Step 2: Tax ── */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-5 h-5 text-primary" />
                <CardTitle>Tax Setup</CardTitle>
              </div>
              <CardDescription>
                Do you collect {locale.taxDefaults.label} or any other form of tax on your services?
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={form2.handleSubmit(onStep2)} className="space-y-6">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => form2.setValue('tax_enabled', false)}
                    className={cn(
                      'rounded-lg border-2 p-5 text-center transition-colors',
                      !taxEnabled
                        ? 'border-primary bg-primary/5 text-primary font-semibold'
                        : 'border-muted hover:border-muted-foreground/40',
                    )}
                  >
                    <div className="text-2xl mb-1">🚫</div>
                    <div className="font-medium">No</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => form2.setValue('tax_enabled', true)}
                    className={cn(
                      'rounded-lg border-2 p-5 text-center transition-colors',
                      taxEnabled
                        ? 'border-primary bg-primary/5 text-primary font-semibold'
                        : 'border-muted hover:border-muted-foreground/40',
                    )}
                  >
                    <div className="text-2xl mb-1">✅</div>
                    <div className="font-medium">Yes</div>
                  </button>
                </div>

                {taxEnabled && (
                  <div className="space-y-4 rounded-lg bg-muted/40 p-4 border">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="tax_rate">Tax Rate (%)</Label>
                        <Input
                          id="tax_rate"
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          {...form2.register('tax_rate', { valueAsNumber: true })}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="tax_label">Tax Label</Label>
                        <Input id="tax_label" placeholder={locale.taxDefaults.label} {...form2.register('tax_label')} />
                        <div className="flex gap-1 flex-wrap">
                          {['GST', 'VAT', 'Sales Tax', 'HST'].map((t) => (
                            <button
                              key={t}
                              type="button"
                              onClick={() => form2.setValue('tax_label', t)}
                              className="text-xs px-2 py-0.5 rounded-full border border-muted-foreground/30 hover:bg-muted transition-colors"
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        id="tax_inclusive"
                        checked={form2.watch('tax_inclusive')}
                        onCheckedChange={(v) => form2.setValue('tax_inclusive', v)}
                      />
                      <Label htmlFor="tax_inclusive" className="cursor-pointer">
                        Tax is included in prices (tax-inclusive)
                      </Label>
                    </div>
                  </div>
                )}

                <div className="space-y-1.5 rounded-lg bg-muted/40 p-4 border">
                  <Label htmlFor="parts_markup_pct">Parts markup (%)</Label>
                  <Input
                    id="parts_markup_pct"
                    type="number"
                    min={0}
                    max={1000}
                    step={0.1}
                    placeholder="0"
                    {...form2.register('parts_markup_pct', {
                      // valueAsNumber turns an empty field into NaN, which the optional number schema rejects
                      setValueAs: (v) => (v === '' || v === null || v === undefined ? undefined : Number(v)),
                    })}
                  />
                  <p className="text-xs text-muted-foreground">
                    When a part is added to a job, the customer price defaults to your cost plus this percentage.
                    You can set a fixed price per part in the catalog and adjust it on any job. The cost price never
                    appears on estimates or invoices.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                    Back
                  </Button>
                  <Button type="submit" className="flex-1" disabled={isSaving}>
                    {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Continue
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* ── Step 3: Metro ── */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-5 h-5 text-primary" />
                <CardTitle>Service Area</CardTitle>
              </div>
              <CardDescription>
                Where does your business operate? Add your first service area.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={form3.handleSubmit(onStep3)} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="metro_name">
                    Area Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="metro_name" placeholder={`e.g., ${locale.addressPlaceholders.locality} metro`} {...form3.register('metro_name')} />
                  {form3.formState.errors.metro_name && (
                    <p className="text-xs text-destructive">{form3.formState.errors.metro_name.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="metro_address">Centre Address</Label>
                  <Input
                    id="metro_address"
                    placeholder={`e.g., 123 Main St, ${locale.addressPlaceholders.locality}`}
                    {...form3.register('metro_address')}
                  />
                  <p className="text-xs text-muted-foreground">Jobs within the radius of this address are assigned to the area.</p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="metro_radius">Radius ({locale.distanceUnit === 'mi' ? 'miles' : 'km'})</Label>
                  <Input
                    id="metro_radius"
                    type="number"
                    min={0}
                    step={1}
                    placeholder={locale.distanceUnit === 'mi' ? '10' : '15'}
                    {...form3.register('metro_radius', { valueAsNumber: true })}
                  />
                </div>

                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1">
                    Back
                  </Button>
                  <Button type="submit" className="flex-1" disabled={isSaving}>
                    {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Continue
                  </Button>
                </div>

                <button
                  type="button"
                  onClick={() => setStep(4)}
                  className="w-full text-sm text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
                >
                  Skip for now
                </button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* ── Step 4: Technician ── */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <Wrench className="w-5 h-5 text-primary" />
                <CardTitle>Your First Technician</CardTitle>
              </div>
              <CardDescription>Add at least one technician to start assigning jobs.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={form4.handleSubmit(onStep4)} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="tech_name">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="tech_name" placeholder="John Smith" {...form4.register('tech_name')} />
                  {form4.formState.errors.tech_name && (
                    <p className="text-xs text-destructive">{form4.formState.errors.tech_name.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="commission_percentage">Commission (%)</Label>
                  <Input
                    id="commission_percentage"
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    {...form4.register('commission_percentage', { valueAsNumber: true })}
                  />
                  {form4.formState.errors.commission_percentage && (
                    <p className="text-xs text-destructive">
                      {form4.formState.errors.commission_percentage.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="tech_phone">
                      Phone{' '}
                      <span className="text-muted-foreground text-xs font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="tech_phone"
                      type="tel"
                      placeholder={locale.phonePlaceholder}
                      {...form4.register('tech_phone')}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="tech_email">
                      Email{' '}
                      <span className="text-muted-foreground text-xs font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="tech_email"
                      type="email"
                      placeholder="john@company.com"
                      {...form4.register('tech_email')}
                    />
                    {form4.formState.errors.tech_email && (
                      <p className="text-xs text-destructive">{form4.formState.errors.tech_email.message}</p>
                    )}
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(3)} className="flex-1">
                    Back
                  </Button>
                  <Button type="submit" className="flex-1" disabled={isSaving}>
                    {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Continue
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* ── Step 5: Google Ads ── */}
        {step === 5 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-5 h-5 text-primary" />
                <CardTitle>Google Ads</CardTitle>
              </div>
              <CardDescription>
                Do you use Google Ads to generate leads for your business?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {!googleAdsYes ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => { void completeSetup(); setStep(6); }}
                      className="rounded-lg border-2 border-muted p-5 text-center hover:border-muted-foreground/40 transition-colors"
                    >
                      <div className="text-2xl mb-1">🚫</div>
                      <div className="font-medium">No</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setGoogleAdsYes(true)}
                      className="rounded-lg border-2 border-muted p-5 text-center hover:border-primary/60 transition-colors"
                    >
                      <div className="text-2xl mb-1">✅</div>
                      <div className="font-medium">Yes</div>
                    </button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(4)}
                    className="w-full"
                  >
                    Back
                  </Button>
                </>
              ) : (
                <>
                  <div className="rounded-lg bg-info/10 border border-info/20 p-4 space-y-2">
                    <p className="font-medium text-info">Connect Google Ads in Settings</p>
                    <p className="text-sm text-info/90">
                      You can link your Google Ads account under{' '}
                      <span className="font-medium">Settings → Integrations</span> to automatically
                      sync spend data and attribute leads to campaigns.
                    </p>
                  </div>
                  <Button
                    className="w-full"
                    onClick={async () => { await completeSetup(); navigate('/settings?tab=integrations'); }}
                  >
                    Go to Integrations
                  </Button>
                  <button
                    type="button"
                    onClick={async () => { await completeSetup(); navigate('/'); }}
                    className="w-full text-sm text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
                  >
                    Continue to Dashboard
                  </button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Step 6: All Set ── */}
        {step === 6 && (
          <Card className="text-center">
            <CardHeader className="pb-2">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
              </div>
              <CardTitle className="text-2xl">You're all set!</CardTitle>
              <CardDescription>Your account is configured and ready to use.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              <Button className="w-full" onClick={async () => { await completeSetup(); navigate('/'); }}>
                Go to Dashboard
              </Button>
              <Button variant="ghost" className="w-full" onClick={async () => { await completeSetup(); navigate('/settings'); }}>
                Review all settings
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
